<template>
  <div class="fill-height paper-background">
    <transition mode="out-in">
      <!-- loaded -->
      <template v-if="SpecialProject && SpecialProject._slug === slug">
        <transition mode="out-in">
          <!-- special project project -->
          <v-row v-if="!about" key="loaded" class="fill-height special-project-gallery-container" align="center" justify="center" no-gutters>
            <!-- gallery type -->
            <v-col cols="12" v-if="SpecialProject.type === 'gallery'" class="special-project-gallery">
              <Scribbles />
              <GalleryHorizontalView routeless seriesless :elements="processedElements" :artist="processedElements[0].photos[0].artist._slug" :title="SpecialProject.sponsorName" />
              <div v-if="$vuetify.breakpoint.smAndDown" class="py-16" />
              <v-btn text small :to="{params: {about: 'about'}}" class="bottom-center-btn text-script">
                <img v-if="SpecialProject.icon" class="brand-icon" ref="icon" :src="`/media/${SpecialProject.icon.id}`">
                <span class="label">About the project</span>
              </v-btn>
            </v-col>
            <!-- modular type -->
            <v-col cols="12" v-if="SpecialProject.type === 'modular'">
              <MomentumScrolling :content-classes="{'paper-background': true, 'py-16': true, 'px-0 px-md-8': true}">
                <div class="d-block spacer" />
                <ClipMask key="clip-mask" class="clip-mask" :label="sponsorName" :masked="false" />
                <Scribbles />
                <v-col cols="12" md="6" offset-md="3" class="px-8 px-md-0 py-0">
                  <v-row justify="center" v-if="SpecialProject.modularContentArtist">
                    <v-col cols="auto" class="text-decoration-underline"><router-link :to="{name: 'Artist', params: {slug: SpecialProject.modularContentArtist._slug}}">{{SpecialProject.modularContentArtist.name}}</router-link></v-col>
                  </v-row>
                  <component
                    v-for="(block, i) in SpecialProject.modularContent"
                    :key="i"
                    :is="repeaterComponents[block._repeaterType]"
                    :data="block"
                    />
                </v-col>
                <v-btn text block small :to="{params: {about: 'about'}}" class="bottom-center-btn text-script">
                  <img v-if="SpecialProject.icon" class="brand-icon" ref="icon" :src="`/media/${SpecialProject.icon.id}`">
                  <span class="label">About the project</span>
                </v-btn>
              </MomentumScrolling>
            </v-col>
            <v-btn text small :to="{name: 'SpecialProjects', params: {about: null}}" class="bottom-left-btn text-script" data-cursor-target="back|center center">Back</v-btn>
          </v-row>
          <!-- about the project -->
          <v-row v-else key="about" class="fill-height" align="center" justify="center" no-gutters>
            <MomentumScrolling :content-classes="{'paper-background': true, 'py-16': true}">
              <Scribbles />
              <v-col cols="12" md="6" offset-md="3" class="px-8 px-md-0 py-0 pt-md-16">
                <component
                  v-for="(block, i) in SpecialProject.about"
                  :key="i"
                  :is="repeaterComponents[block._repeaterType]"
                  :data="block"
                  />
              </v-col>
            </MomentumScrolling>
            <v-btn text small :to="{params: {about: null}}" class="bottom-left-btn text-script" data-cursor-target="back|center center">Back</v-btn>
          </v-row>
        </transition>
      </template>
      <!-- loading -->
      <v-row v-else class="fill-height" align="center" justify="center" key="loading">
        <Scribbles />
        <v-col cols="auto" >
          <div class="animated-eye" />
        </v-col>
      </v-row>
    </transition>
  </div>
</template>

<script>
import SVGInject from '@iconfu/svg-inject'
import delay from 'lodash/delay'
import GetSpecialProjectGallery from '@/graphql/GetSpecialProjectGallery.gql'
import GetSpecialProjectModular from '@/graphql/GetSpecialProjectModular.gql'
import Scribbles from '@/molecules/Scribbles'
import GalleryHorizontalView from '@/molecules/GalleryHorizontalView'
import ContentParagraphWithTitle from '@/atoms/ContentParagraphWithTitle'
import ClipMask from '@/molecules/ClipMask'
import ContentTitle from '@/atoms/ContentTitle'
import ContentImage from '@/atoms/ContentImage'
import ContentVideo from '@/atoms/ContentVideo'
import MomentumScrolling from '@/atoms/MomentumScrolling'
import { optimizedImageURL } from '@/services/OptimizedImage'

export default {
  name: 'SpecialProject',
  components: {
    // SocialRepeater,
    MomentumScrolling,
    GalleryHorizontalView,
    Scribbles,
    ClipMask
  },
  props: {
    type: {
      type: String,
      required: true
    },
    slug: {
      type: String,
      required: true
    },
    about: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      queries: {
        gallery: GetSpecialProjectGallery,
        modular: GetSpecialProjectModular
      },
      repeaterComponents: {
        Image: ContentImage,
        Paragraph: ContentParagraphWithTitle,
        Title: ContentTitle,
        Video: ContentVideo
      }
    }
  },
  watch: {
    about (value) {
      delay(() => this.$vuetify.goTo(0), 1000)
    },
    SpecialProject (value) {
      this.$root.sponsorLogo = value.logo
    }
  },
  destroyed () {
    this.$root.sponsorLogo = null
  },
  apollo: {
    SpecialProject: {
      query () {
        return this.queries[this.type]
      },
      variables () {
        return {
          status: 'PUBLISHED',
          slug: this.slug
        }
      },
      result () {
        // console.log(this.SpecialProject)
        this.$nextTick(this.parseSVGs)
        this.$root.$emit('scroll-to-saved-position')
      }
    }
  },
  computed: {
    processedElements () {
      if (this.SpecialProject && this.SpecialProject.gallery && this.SpecialProject.gallery.length > 0) {
        const ret = []
        const regex = /%([0-9])%/gm

        for (let i = 0; i < this.SpecialProject.gallery.length; i++) {
          const element = this.SpecialProject.gallery[i]
          const match = regex.exec(element.details.series)
          if (match) {
            const numPhotos = parseInt(match[1])
            ret.push({
              id: `element-${i}`, // first photo id
              isCollage: true,
              photos: this.SpecialProject.gallery.slice(i, i + numPhotos)
            })
            i += numPhotos
          } else {
            ret.push({
              id: `element-${i}`,
              isCollage: false,
              photos: [element]
            })
          }
        }
        return ret
      }
      return []
    },

    sponsorName () {
      const sponsor = this.SpecialProject.sponsorName.toLowerCase().indexOf('lavazza') !== -1 ? this.SpecialProject.sponsorName.split(' ').slice(0, 3).join(' ') : this.SpecialProject.sponsorName
      return sponsor
    }
  },
  metaInfo () {
    if (this.SpecialProject) {
      let meta = [
        { vmid: 'ogtitle', property: 'og:title', itemprop: 'name', content: `${this.SpecialProject.title} | PhotoVogue Festival 2020` },
        { vmid: 'description', name: 'description', content: this.SpecialProject.shareDescription },
        { vmid: 'ogdescription', property: 'og:description', content: this.SpecialProject.shareDescription }
      ]
      if (this.SpecialProject.shareImage) {
        meta = meta.concat([
          { vmid: 'ogimage', property: 'og:image', content: optimizedImageURL(this.SpecialProject.shareImage, { width: 1200, height: 625, format: 'jpg', fit: 'crop' }) },
          { vmid: 'ogimagetype', property: 'og:image:type', content: 'image/jpeg' },
          { vmid: 'ogimageheight', property: 'og:image:width', content: '1200' },
          { vmid: 'ogimagewidth', property: 'og:image:height', content: '675' }
        ])
      }
      return {
        // about page browser title
        title: this.SpecialProject.title,
        meta
      }
    }
  },
  methods: {
    parseSVGs () {
      const { icon } = this.$refs
      if (icon) SVGInject(icon)
    }
  }
}
</script>

<style lang="scss" scoped>

.spacer {
  padding-bottom: 3rem;

  @include breakpoint(md-and-up) {
    padding-bottom: 12rem;
  }
}

::v-deep .clip-mask {
  @include breakpoint(sm-and-down) {
    top: 60px;

    p {
      font-size: 10vw;
    }
  }
}

::v-deep .bottom-center-btn {
  &:hover {
    opacity: 1;

    .label {
      opacity: 0.5;
    }

    .brand-icon {
      opacity: 1 !important;
      transform: translate(-50%, -50%) scale(0.8) rotate(-10deg);
    }

    path {
      fill: map-deep-get($colors, 'red', 'base') !important;
    }
  }

  .label {
    transition: opacity 1s $ease-out-expo;
  }
}

::v-deep .brand-icon {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform 1s $ease-out-expo, opacity 1s $ease-out-expo;
  opacity: 0.15;
  width: 220px;

  @include breakpoint(md-and-up) {
    width: 280px;
  }

  path {
    transition: fill 1s $ease-out-expo;
    fill: map-get($shades, 'black');
  }
}

.special-project-gallery-container {
  overflow-x: hidden;
}
</style>
